import React from 'react'
import classNames from 'classnames'
import { IconX } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'

import './Banner.scss'

/**
 * @import import { Banner } from '@/components/banners/Banner'
 * @param {object} props
 * @param {boolean} [props.emphasized] Gray emphasis background
 * @param {boolean} [props.brand] Brand color scheme
 * @param {boolean} [props.success] Success color scheme
 * @param {boolean} [props.warning] Warning color scheme
 * @param {boolean} [props.caution] Caution color scheme
 * @param {boolean} [props.critical] Critical color scheme
 * @param {boolean} [props.informative] Informative color scheme
 * @param {boolean} [props.shrunk] Adds outer margin for banners inset in a panel
 * @param {boolean} [props.shrunkHeight] Removes the top margin
 * @param {boolean} [props.magical] AI (violet) color scheme
 * @param {node} [props.header] Heading rendered above the content
 * @param {node} [props.icon] Icon in a leading column next to the content
 * @param {boolean} [props.large] More padding, larger body text, and a bolder heading
 * @param {boolean} [props.overlay] Fixed position, centered over the page
 * @param {boolean} [props.raised] Drop shadow
 * @param {node} [props.content] Banner body; takes precedence over children
 * @param {node} [props.children] Banner body when content is not set
 * @param {string} [props.className] Additional classes on the banner element
 * @param {string} [props.dataTestId] Overrides the default banner test ids
 * @param {Function} [props.onClose] Shows an X button in the corner that calls this
 */
export const Banner = ({
  emphasized,
  brand,
  success,
  warning,
  caution,
  critical,
  informative,
  shrunk,
  shrunkHeight,
  magical,
  header,
  icon,
  large,
  overlay,
  raised,
  content,
  children,
  className,
  dataTestId,
  onClose,
  ...props
}) => {
  return (
    <div
      data-testid={dataTestId || 'banner'}
      className={classNames('banner', className, {
        emphasized,
        brand,
        success,
        warning,
        caution,
        critical,
        informative,
        shrunk,
        magical,
        icon,
        large,
        overlay,
        raised,
        'shrunk-height': shrunkHeight,
      })}
      {...props}
    >
      {icon}
      {header && (
        <header data-testid={dataTestId ? `${dataTestId}-header` : 'header'}>
          <h2 className={classNames({ 'shrunk-height': shrunkHeight })}>{header}</h2>
        </header>
      )}

      {content || (children && <div className="banner__content">{content || children}</div>)}

      {onClose && (
        <Button
          basic
          square
          small
          className="banner__close"
          onClick={onClose}
          aria-label="Close banner"
          data-testid={dataTestId ? `${dataTestId}-close` : 'banner-close'}
        >
          <IconX className="icon-svg" />
        </Button>
      )}
    </div>
  )
}
